import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Globe,
  GlobeLock,
  Loader2,
  Video,
  Image,
  FileText,
  Sparkles,
  Tag,
  Copy,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TemplateDetail() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { isAdmin } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/templates/${templateId}`, {
        withCredentials: true,
      });
      setTemplate(response.data);
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("Failed to load template");
      navigate("/admin/templates");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/templates")} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <h1 className="text-lg font-semibold truncate max-w-[300px]">
                {template.title || "Untitled Template"}
              </h1>
              <Badge
                variant={template.status === "published" ? "default" : "secondary"}
                className={template.status === "published" ? "bg-green-500" : "bg-amber-500"}
              >
                {template.status === "published" ? <Globe className="w-3 h-3 mr-1" /> : <GlobeLock className="w-3 h-3 mr-1" />}
                {template.status}
              </Badge>
            </div>
            {isAdmin && (
              <Button onClick={() => navigate(`/admin/templates/${templateId}/edit`)} data-testid="edit-template-btn">
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Meta info */}
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span>Type: <strong className="text-foreground">{template.template_type?.toUpperCase()}</strong></span>
          <span>Ratio: <strong className="text-foreground">{template.aspect_ratio}</strong></span>
          <span>Uses: <strong className="text-foreground">{template.usage_count || 0}</strong></span>
          <span>ID: <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{template.id}</code></span>
        </div>

        {/* Description */}
        {template.description && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
            <p className="text-base">{template.description}</p>
          </div>
        )}

        {/* Tags */}
        {template.tags?.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {template.tags.map((tag) => (
                <Badge key={tag} variant="outline"><Tag className="w-3 h-3 mr-1" />{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Images Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Avatar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Image className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Avatar</h3>
              </div>
              {template.avatar_image ? (
                <img src={template.avatar_image} alt="Avatar" className="w-full rounded-lg border" data-testid="detail-avatar" />
              ) : (
                <div className="aspect-[9/16] bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">Not uploaded</div>
              )}
            </CardContent>
          </Card>

          {/* Background */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Image className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Background</h3>
              </div>
              {template.background_image ? (
                <img src={template.background_image} alt="Background" className="w-full rounded-lg border" data-testid="detail-background" />
              ) : (
                <div className="aspect-[9/16] bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">Not uploaded</div>
              )}
            </CardContent>
          </Card>

          {/* Product */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Image className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Sample Product</h3>
              </div>
              {template.sample_product_image ? (
                <img src={template.sample_product_image} alt="Product" className="w-full rounded-lg border" data-testid="detail-product" />
              ) : (
                <div className="aspect-[9/16] bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">Not uploaded</div>
              )}
            </CardContent>
          </Card>

          {/* Composite */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Composite</h3>
              </div>
              {template.composite_image ? (
                <img src={template.composite_image} alt="Composite" className="w-full rounded-lg border" data-testid="detail-composite" />
              ) : (
                <div className="aspect-[9/16] bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">Not generated</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Composite Prompt & Seed */}
        {(template.composite_prompt || template.composite_seed != null) && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Composite Generation Config</h3>
              </div>
              {template.composite_prompt && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Prompt</span>
                    <button onClick={() => copyToClipboard(template.composite_prompt, "Prompt")} className="text-xs text-primary hover:underline flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <p className="text-sm bg-muted/50 rounded-lg p-3 whitespace-pre-wrap">{template.composite_prompt}</p>
                </div>
              )}
              {template.composite_seed != null && (
                <div>
                  <span className="text-xs text-muted-foreground">Seed: </span>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{template.composite_seed}</code>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Script */}
        {template.script && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">Script</h3>
                </div>
                <button onClick={() => copyToClipboard(template.script, "Script")} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <p className="text-sm bg-muted/50 rounded-lg p-3 whitespace-pre-wrap">{template.script}</p>
            </CardContent>
          </Card>
        )}

        {/* i2v Prompt */}
        {template.i2v_prompt && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">i2v Prompt</h3>
                </div>
                <button onClick={() => copyToClipboard(template.i2v_prompt, "i2v Prompt")} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <p className="text-sm bg-muted/50 rounded-lg p-3 whitespace-pre-wrap font-mono">{template.i2v_prompt}</p>
            </CardContent>
          </Card>
        )}

        {/* Sample Video */}
        {template.sample_video && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Video className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Sample Video</h3>
              </div>
              <video src={template.sample_video} controls className="w-full max-h-[500px] rounded-lg" data-testid="detail-video" />
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
